import React, { useState } from "react";
import ComponentTypeBadge from "../configurePluginOrScript/ComponentTypeBadge";
import styled, { css } from "styled-components";
import {
  Banner,
  Button,
  Checkbox,
  Input,
  InputGroup,
  Select,
  SelectOption,
  Table,
} from "@cinesys/ui/components";
import { useMutation, useQueryClient } from "react-query";
import ApiService from "../../services/ApiService";
import { Play } from "react-feather";
import CronInput from "../configurePluginOrScript/CronInput";
import { developerToolsEnabled } from "../../services/env";
import { isUserVisibleComponent } from "@cinesys/shared";
import cronstrue from "cronstrue";

interface Props {
  pluginInstance: PluginInstanceItem;
  pluginConfig: PluginConfigItem;
  onChange: (pluginInstance: PluginInstanceItem) => void;
}

// Three-column header: Component | Last Executed | Enable
const columns = [
  { title: "Component", width: "1fr" },
  { title: "Last Executed", width: "1fr" },
  { title: "Enable", width: "60px" },
];

// The top row spans all three columns for label input
const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${columns.map(({ width }) => width).join(" ")};
  column-gap: 10px;
  align-items: center;
`;

// Component row: three columns on the first line, then a full-width options row below
const StyledRow = styled.div`
  display: grid;
  grid-template-columns: ${columns.map(({ width }) => width).join(" ")};
  column-gap: 10px;
  align-items: center;
`;

// Full-width options area spanning all three columns
const OptionsRow = styled.div`
  grid-column: 1 / -1;
  margin-top: 8px;
  padding: 0 2px;
`;

// Two-up input layout for components with exactly two inputs
const TwoUpInputs = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 10px;

  > div {
    margin-bottom: 5px;
  }
`;

// Single-input layout
const SingleInput = styled.div`
  > div {
    margin-bottom: 5px;
  }
`;

const Title = styled.div`
  display: flex;
  align-items: center;

  > * + * {
    margin-left: 5px;
  }
`;

const LastExecuted = styled.div`
  font-size: 12px;
  color: var(--gray400);
`;

// G6: include seconds in last-run timestamp.
const formatLastExecuted = (ts: string | undefined): string => {
  if (!ts) {
    return "Never";
  }
  return new Date(ts).toLocaleString("en-US", {
    year: "2-digit",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  });
};

/** Derive a human-readable cron label using the same logic as CronInput.
 *  Returns undefined when cronstrue is unavailable or the expression is invalid. */
const formatCronLabel = (cron: string | undefined): string | undefined => {
  if (!cron) {
    return undefined;
  }
  try {
    if (cron.trim().split(/ +/g).length !== 6) {
      return undefined;
    }
    return cronstrue.toString(cron) + " (America/Chicago)";
  } catch {
    return undefined;
  }
};

const PluginInstanceComponent = (props: {
  component: PluginConfigItem["components"][0];
  pluginInstance: PluginInstanceItem;
  onChange: (pluginInstance: PluginInstanceItem) => void;
}) => {
  const { component, pluginInstance, onChange } = props;
  const titleInstance = pluginInstance.titles?.find(
    ({ customActionConfigId }) => customActionConfigId === component.id
  );
  const aclGroupsInstance = pluginInstance.acl_groups?.find(
    ({ customActionConfigId }) => customActionConfigId === component.id
  );
  const cronInstance = pluginInstance.crons?.find(
    ({ scheduleConfigId }) => scheduleConfigId === component.id
  );
  const lastExecutedAt = pluginInstance.componentLastExecutedAt?.[component.id];
  const queryClient = useQueryClient();

  const runMutation = useMutation({
    mutationFn: async ({
      componentId,
      local,
    }: {
      componentId: string;
      local?: boolean;
    }) => {
      await ApiService.fetch(
        "POST",
        `/api/run/${pluginInstance.id}/${componentId}${
          local ? "?local=true" : ""
        }`,
        {
          system_domain_id: pluginInstance.systemDomainId,
        }
      );
      queryClient.invalidateQueries(["jobList"]);
    },
  });

  // Determine what options to render below the main row
  const renderOptions = () => {
    if (component.type === "custom_action_config") {
      // Two inputs — lay them side by side
      return (
        <TwoUpInputs>
          <Input
            label="Custom Title"
            key={component.id}
            value={titleInstance?.title ?? ""}
            placeholder={component.title}
            onChange={(value) => {
              const obj = {
                customActionConfigId: component.id,
                title: value,
              };
              if (titleInstance) {
                onChange({
                  ...pluginInstance,
                  titles: (pluginInstance.titles ?? []).map((t) =>
                    t.customActionConfigId === component.id ? obj : t
                  ),
                });
              } else {
                onChange({
                  ...pluginInstance,
                  titles: [...(pluginInstance.titles ?? []), obj],
                });
              }
            }}
          />
          <Select
            optionsAsync={{
              key: `${pluginInstance.systemDomainId}/team`,
              fn: async () =>
                (
                  await ApiService.fetch<{ data: SelectOption[] }>(
                    "GET",
                    `/api/select_option/${pluginInstance.systemDomainId}/team`
                  )
                ).data,
            }}
            value={aclGroupsInstance?.groups}
            isMulti
            label="ACL Teams"
            onClear={() => {
              onChange({
                ...pluginInstance,
                acl_groups: pluginInstance.acl_groups?.filter(
                  ({ customActionConfigId }) =>
                    customActionConfigId !== component.id
                ),
              });
            }}
            onChange={(value) => {
              const obj = {
                customActionConfigId: component.id,
                groups: value.map(({ value }) => value ?? ""),
              };
              if (aclGroupsInstance) {
                onChange({
                  ...pluginInstance,
                  acl_groups: (pluginInstance.acl_groups ?? []).map((a) =>
                    a.customActionConfigId === component.id ? obj : a
                  ),
                });
              } else {
                onChange({
                  ...pluginInstance,
                  acl_groups: [...(pluginInstance.acl_groups ?? []), obj],
                });
              }
            }}
            portalTarget={document.getElementById("PortalTarget")}
            placeholder="Everyone"
          />
        </TwoUpInputs>
      );
    }

    if (component.type === "schedule_config") {
      // Buttons on top, cron input below
      return (
        <SingleInput>
          <div style={{ display: "flex", gap: 5, marginBottom: 8 }}>
            <Button
              label="Run Now"
              type="success"
              icon={Play}
              onClick={() => runMutation.mutate({ componentId: component.id })}
              loading={runMutation.isLoading}
            />
            {developerToolsEnabled && (
              <Button
                label="Run Local"
                type="default"
                borderless
                onClick={() =>
                  runMutation.mutate({
                    componentId: component.id,
                    local: true,
                  })
                }
                loading={runMutation.isLoading}
              />
            )}
            {runMutation.isError && <Banner error="Error running" />}
          </div>
          <CronInput
            value={cronInstance?.cron ?? component.cron}
            onChange={(value) => {
              const obj = {
                scheduleConfigId: component.id,
                cron: value,
              };
              if (cronInstance) {
                onChange({
                  ...pluginInstance,
                  crons: (pluginInstance.crons ?? []).map((c) =>
                    c.scheduleConfigId === component.id ? obj : c
                  ),
                });
              } else {
                onChange({
                  ...pluginInstance,
                  crons: [...(pluginInstance.crons ?? []), obj],
                });
              }
            }}
          />
        </SingleInput>
      );
    }

    return null;
  };

  const options = renderOptions();

  return (
    <>
      <StyledRow>
        <Title>
          <ComponentTypeBadge type={component.type} />
          <span>{component.title}</span>
        </Title>
        <LastExecuted>{formatLastExecuted(lastExecutedAt)}</LastExecuted>
        <Checkbox
          value={!pluginInstance.disabledComponents?.includes(component.id)}
          onChange={(enabled) => {
            if (enabled) {
              onChange({
                ...pluginInstance,
                disabledComponents: pluginInstance.disabledComponents?.filter(
                  (id) => id !== component.id
                ),
              });
            } else {
              onChange({
                ...pluginInstance,
                disabledComponents: [
                  ...(pluginInstance.disabledComponents ?? []),
                  component.id,
                ],
              });
            }
          }}
        />
        {options && <OptionsRow>{options}</OptionsRow>}
      </StyledRow>
    </>
  );
};

const PluginLabelComponent = (props: {
  pluginInstance: PluginInstanceItem;
  onChange: (pluginInstance: PluginInstanceItem) => void;
}) => {
  const { pluginInstance, onChange } = props;
  return (
    <HeaderRow>
      <Title>
        <ComponentTypeBadge type="iconik_plugin_config" />
      </Title>
      <div />
      <div />
      <OptionsRow>
        <Input
          label="Label"
          value={pluginInstance.label ?? ""}
          onChange={(value) => onChange({ ...pluginInstance, label: value })}
          noPadding
        />
      </OptionsRow>
    </HeaderRow>
  );
};

const Wrapper = styled.div``;

const PluginInstanceComponentList = (props: Props) => {
  const { pluginInstance, pluginConfig, onChange } = props;

  return (
    <Wrapper>
      <Table
        style={
          {
            "--tableHeaderBg": "transparent",
            "--tableHeaderBorderBottom": "1px solid var(--dark300)",
            "--tableHeaderShadow": "none",
          } as React.CSSProperties
        }
        columns={columns}
        rows={[
          {
            id: "plugin",
            element: () => (
              <PluginLabelComponent
                pluginInstance={pluginInstance}
                onChange={onChange}
              />
            ),
          },
          ...pluginConfig.components
            .filter(isUserVisibleComponent)
            .map((component) => ({
              id: component.id,
              element: () => (
                <PluginInstanceComponent
                  component={component}
                  pluginInstance={pluginInstance}
                  onChange={onChange}
                />
              ),
            })),
        ]}
        autoHeight={{ twoColumn: true }}
      />
    </Wrapper>
  );
};

export default PluginInstanceComponentList;

// ---------------------------------------------------------------------------
// PluginInstanceComponentSubgroups — each component as a collapsible InputGroup
// embedded at the bottom of the settings editor (replaces the Components tab).
// ---------------------------------------------------------------------------

const SubgroupWrapper = styled.div`
  margin: 0 10px;
`;

// #6: component title font — matches InputGroup's own 13px title-text size (was 15px, read too
// big next to every other InputGroup/Section header in the app).
const SubgroupTitle = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;

  ${(props) =>
    props.$disabled &&
    css`
      color: var(--gray100);
    `}
`;

// G3: custom title + sub-label baseline-align.
const CustomTitleWrapper = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const CustomTitleMain = styled.span`
  font-size: 13px;
`;

const CustomTitleSub = styled.span`
  font-size: 13px;
  color: var(--dark700);
`;

const SubgroupButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

// Last-run + cron-description row — always visible in body (collapsed or expanded).
const BodyMetaRow = styled.div`
  padding: 8px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BodyMetaItem = styled.span`
  font-size: 13px;
  color: var(--dark700);
  white-space: nowrap;
`;

// MDV-style field rows inside component body — matches VariableInput layout.
const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 2.5fr;
  column-gap: 16px;
  align-items: center;
  padding: 3px 10px;
`;

const FieldLabel = styled.span`
  color: var(--gray200);
  font-size: 14px;
  text-align: right;
  align-self: center;
  word-break: break-word;
`;

const EditWrapper = styled.div`
  --inputBorder: var(--dark300);
  --inputBackground: var(--dark200);

  input:not(.react-select__input),
  textarea {
    height: 30px;
    font-size: 15px;
  }

  .react-select__control {
    min-height: 30px;
    font-size: 15px;
    flex-grow: 1;
  }
`;

const SELECT_STYLES = { minHeight: 30, fontSize: 15 };
const INPUT_SIZE = { buttonSize: 26, minWidth: 50 } as const;

interface SubgroupsProps {
  pluginInstance: PluginInstanceItem;
  pluginConfig: PluginConfigItem;
  onChange: (pluginInstance: PluginInstanceItem) => void;
}

const PluginInstanceComponentSubgroup = (props: {
  component: PluginConfigItem["components"][0];
  pluginInstance: PluginInstanceItem;
  onChange: (pluginInstance: PluginInstanceItem) => void;
}) => {
  const { component, pluginInstance, onChange } = props;
  const [collapsed, setCollapsed] = useState(true);

  const titleInstance = pluginInstance.titles?.find(
    ({ customActionConfigId }) => customActionConfigId === component.id
  );
  const aclGroupsInstance = pluginInstance.acl_groups?.find(
    ({ customActionConfigId }) => customActionConfigId === component.id
  );
  const cronInstance = pluginInstance.crons?.find(
    ({ scheduleConfigId }) => scheduleConfigId === component.id
  );
  const lastExecutedAt = pluginInstance.componentLastExecutedAt?.[component.id];
  const queryClient = useQueryClient();

  const runMutation = useMutation({
    mutationFn: async ({
      componentId,
    }: {
      componentId: string;
      local?: boolean;
    }) => {
      await ApiService.fetch(
        "POST",
        `/api/run/${pluginInstance.id}/${componentId}`,
        { system_domain_id: pluginInstance.systemDomainId }
      );
      queryClient.invalidateQueries(["jobList"]);
    },
  });

  // #12: enable toggle always visible (form is always editable).
  const enabled = !pluginInstance.disabledComponents?.includes(component.id);

  // G3: For custom_action_config with a custom title set, show the custom title
  // as the primary label and the original title as a smaller sub-label.
  const customTitle = titleInstance?.title;
  let displayTitle =
    component.type === "custom_action_config" && customTitle
      ? customTitle
      : component.title;
  if (!enabled) {
    displayTitle += " (Disabled)";
  }
  const subLabel =
    component.type === "custom_action_config" && customTitle
      ? component.title
      : undefined;

  // G7: For schedule_config, derive the "Runs every…" cron description.
  const activeCron =
    component.type === "schedule_config"
      ? cronInstance?.cron ?? (component as { cron?: string }).cron
      : undefined;
  const cronLabel = formatCronLabel(activeCron);

  // #6/#★: Title bar — badge + title (+ sub-label). 13px font.
  // "Last run" and cron label move to the collapsed BODY (#7).
  const titleBar = (
    <SubgroupTitle $disabled={!enabled}>
      <ComponentTypeBadge type={component.type} />
      {subLabel ? (
        <CustomTitleWrapper>
          <CustomTitleMain>{displayTitle}</CustomTitleMain>
          <CustomTitleSub>{subLabel}</CustomTitleSub>
        </CustomTitleWrapper>
      ) : (
        <span>{displayTitle}</span>
      )}
    </SubgroupTitle>
  );

  const titleButtons = (
    <SubgroupButtons>
      {/* G2: Run Now only — Run Local removed entirely (unless dev tools enabled) */}
      {component.type === "schedule_config" && (
        <>
          <Button
            label="Run Now"
            type="success"
            icon={Play}
            size={30}
            onClick={(e) => {
              e.stopPropagation();
              runMutation.mutate({ componentId: component.id });
            }}
            loading={runMutation.isLoading}
          />
          {runMutation.isError && <Banner error="Error running" />}
        </>
      )}
      {/* #12: enable toggle always visible */}
      <Checkbox
        value={enabled}
        onChange={(isEnabled) => {
          if (isEnabled) {
            onChange({
              ...pluginInstance,
              disabledComponents: pluginInstance.disabledComponents?.filter(
                (id) => id !== component.id
              ),
            });
          } else {
            onChange({
              ...pluginInstance,
              disabledComponents: [
                ...(pluginInstance.disabledComponents ?? []),
                component.id,
              ],
            });
          }
        }}
      />
    </SubgroupButtons>
  );

  // Last-run + schedule rate — always visible for all component types.
  const metaBody = enabled ? (
    <BodyMetaRow>
      <BodyMetaItem>
        Last run: {formatLastExecuted(lastExecutedAt)}
      </BodyMetaItem>
      {cronLabel && <BodyMetaItem>{cronLabel}</BodyMetaItem>}
    </BodyMetaRow>
  ) : undefined;

  // Editable fields shown only when expanded.
  const expandedBody = (() => {
    if (component.type === "custom_action_config") {
      return (
        <>
          <FieldRow>
            <FieldLabel>Custom Title</FieldLabel>
            <EditWrapper>
              <Input
                value={titleInstance?.title ?? ""}
                placeholder={component.title}
                onChange={(value) => {
                  const obj = {
                    customActionConfigId: component.id,
                    title: value,
                  };
                  if (titleInstance) {
                    onChange({
                      ...pluginInstance,
                      titles: (pluginInstance.titles ?? []).map((t) =>
                        t.customActionConfigId === component.id ? obj : t
                      ),
                    });
                  } else {
                    onChange({
                      ...pluginInstance,
                      titles: [...(pluginInstance.titles ?? []), obj],
                    });
                  }
                }}
                {...INPUT_SIZE}
              />
            </EditWrapper>
          </FieldRow>
          <FieldRow>
            <FieldLabel>ACL Teams</FieldLabel>
            <EditWrapper>
              <Select
                optionsAsync={{
                  key: `${pluginInstance.systemDomainId}/team`,
                  fn: async () =>
                    (
                      await ApiService.fetch<{ data: SelectOption[] }>(
                        "GET",
                        `/api/select_option/${pluginInstance.systemDomainId}/team`
                      )
                    ).data,
                }}
                value={aclGroupsInstance?.groups}
                isMulti
                onClear={() => {
                  onChange({
                    ...pluginInstance,
                    acl_groups: pluginInstance.acl_groups?.filter(
                      ({ customActionConfigId }) =>
                        customActionConfigId !== component.id
                    ),
                  });
                }}
                onChange={(value) => {
                  const obj = {
                    customActionConfigId: component.id,
                    groups: value.map(({ value }) => value ?? ""),
                  };
                  if (aclGroupsInstance) {
                    onChange({
                      ...pluginInstance,
                      acl_groups: (pluginInstance.acl_groups ?? []).map((a) =>
                        a.customActionConfigId === component.id ? obj : a
                      ),
                    });
                  } else {
                    onChange({
                      ...pluginInstance,
                      acl_groups: [...(pluginInstance.acl_groups ?? []), obj],
                    });
                  }
                }}
                portalTarget={document.getElementById("PortalTarget")}
                placeholder="Everyone"
                containerStyles={SELECT_STYLES}
                buttonSize={26}
              />
            </EditWrapper>
          </FieldRow>
        </>
      );
    }

    if (component.type === "schedule_config") {
      return (
        <CronInput
          value={
            cronInstance?.cron ?? (component as { cron?: string }).cron ?? ""
          }
          onChange={(value) => {
            const obj = {
              scheduleConfigId: component.id,
              cron: value,
            };
            if (cronInstance) {
              onChange({
                ...pluginInstance,
                crons: (pluginInstance.crons ?? []).map((c) =>
                  c.scheduleConfigId === component.id ? obj : c
                ),
              });
            } else {
              onChange({
                ...pluginInstance,
                crons: [...(pluginInstance.crons ?? []), obj],
              });
            }
          }}
        />
      );
    }

    return null;
  })();

  // Only show collapse arrow when there are editable fields to expand into.
  const hasExpandedBody = expandedBody !== null;

  return (
    <InputGroup
      collapsible={hasExpandedBody}
      collapsed={hasExpandedBody ? collapsed : undefined}
      setCollapsed={hasExpandedBody ? setCollapsed : undefined}
      title={titleBar}
      buttons={titleButtons}
      noPad
      collapsedChildren={hasExpandedBody ? metaBody : undefined}
    >
      {metaBody}
      {expandedBody}
    </InputGroup>
  );
};

/**
 * Renders each user-visible plugin component as a collapsible subgroup.
 * Intended for the bottom of the settings editor (replaces the Components tab).
 */
export const PluginInstanceComponentSubgroups = (props: SubgroupsProps) => {
  const { pluginInstance, pluginConfig, onChange } = props;

  const visibleComponents = pluginConfig.components.filter(
    isUserVisibleComponent
  );

  if (visibleComponents.length === 0) {
    return null;
  }

  return (
    <SubgroupWrapper data-testid="component-subgroups">
      {visibleComponents.map((component) => (
        <PluginInstanceComponentSubgroup
          key={component.id}
          component={component}
          pluginInstance={pluginInstance}
          onChange={onChange}
        />
      ))}
    </SubgroupWrapper>
  );
};
